import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login, register, logout, getInfo, sendSMS, bindPhoneForWechatUser, wechatLogin } from '@/api/auth'
import { generateReferralLink as apiGenerateReferralLink, validateReferral as apiValidateReferral, getReferralStats as apiGetReferralStats, getReferrals as apiGetReferrals } from '@/api/referral'

export const useUserStore = defineStore('user', () => {
  // 状态
  const token = ref(localStorage.getItem('token') || '')
  const userInfo = ref(null)
  
  // 计算属性
  const isLoggedIn = computed(() => !!token.value)
  
  // 检查是否是微信用户（有微信openid但没有绑定手机号）
  const isWechatUser = computed(() => {
    return userInfo.value && 
           userInfo.value.wechat_open_id && 
           userInfo.value.wechat_open_id !== '' &&
           (!userInfo.value.phone || userInfo.value.phone === '')
  })
  // 设置token
  const setToken = (newToken) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
      // 设置过期时间（7天，与后端JWT过期时间一致）
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      localStorage.setItem('token_expires', expiresAt)
    } else {
      localStorage.removeItem('token')
      localStorage.removeItem('token_expires')
    }
  }
  
  // 设置用户信息
  const setUserInfo = (newUserInfo) => {
    userInfo.value = newUserInfo
  }
  
  // 清除token
  const clearToken = () => {
    token.value = ''
    localStorage.removeItem('token')
    localStorage.removeItem('token_expires')
  }
  
  // 清除用户信息
  const clearUserInfo = () => {
    userInfo.value = null
  }
  
  // 用户登录
  const handleLogin = async (userInfo) => {
    try {
      let response
      // 根据登录类型选择不同的接口
      if (userInfo.login_type === 3) {
        // 微信登录
        response = await wechatLogin(userInfo)
      } else {
        // 普通登录
        response = await login(userInfo)
      }
      
      // 检查响应是否包含错误标记
      if (response.error === true) {
        // 返回包含错误信息的对象，而不是抛出异常
        return { success: false, message: response.message || '登录失败' }
      }
      
      const { data } = response
      setToken(data.token)
      setUserInfo(data.user)
      
      // 返回完整的登录结果，包括是否需要绑定手机号等信息
      return { 
        success: true, 
        data: data,
        needBindPhone: data.need_bind_phone || false
      }
    } catch (error) {
      // 捕获网络错误等其他异常
      console.error('登录失败:', error)
      return { success: false, message: error.message || '登录失败' }
    }
  }
  
  // 用户注册
  const handleRegister = async (userInfo) => {
    try {
      const response = await register(userInfo)
      
      // 检查响应是否包含错误标记
      if (response.error === true) {
        // 返回包含错误信息的对象，而不是抛出异常
        return { success: false, message: response.message || '注册失败' }
      }
      
      const { data } = response
      setToken(data.token)
      setUserInfo(data.user)
      return { success: true, data: data }
    } catch (error) {
      // 捕获网络错误等其他异常
      console.error('注册失败:', error)
      return { success: false, message: error.message || '注册失败' }
    }
  }
  
  // 用户退出
  const handleLogout = async () => {
    try {
      await logout()
      clearToken()
      clearUserInfo()
      
      // 清除推荐注册相关的localStorage数据
      localStorage.removeItem('referrer_id')
      
      return { success: true, message: '退出成功' }
    } catch (error) {
      // 即使API调用失败，也要清除本地token和用户信息
      clearToken()
      clearUserInfo()
      
      // 清除推荐注册相关的localStorage数据
      localStorage.removeItem('referrer_id')
      
      // 转换英文错误信息为中文
      let errorMessage = '退出登录失败'
      if (error.response && error.response.status === 404) {
        errorMessage = '网络请求失败，请稍后重试'
      } else if (error.message && error.message.includes('Request failed with status code')) {
        errorMessage = '网络请求失败，请稍后重试'
      }
      
      return { success: false, message: errorMessage }
    }
  }
  
  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const response = await getInfo()
      const { data } = response
      setUserInfo(data)
      return data
    } catch (error) {
      console.error('获取用户信息失败:', error)
      // 清除用户信息，避免死循环
      clearUserInfo()
      throw error // 重新抛出异常，让调用者知道获取失败
    }
  }
  
  // 发送短信验证码
  const handleSendSMS = async (data) => {
    const response = await sendSMS(data)
    
    // 检查响应是否包含错误
    if (response.code && response.code !== 200) {
      throw new Error(response.message || '发送验证码失败')
    }
    
    return response
  }

  // 微信用户绑定手机号
  const handleBindPhoneForWechatUser = async (data) => {
    try {
      const response = await bindPhoneForWechatUser(data)
      
      // 检查响应是否包含错误标记
      if (response.error === true) {
        return { success: false, message: response.message || '绑定失败' }
      }
      
      // 绑定成功后，更新本地用户信息
      if (userInfo.value) {
        userInfo.value.phone = data.phone
      }
      
      return { success: true, message: '绑定成功' }
    } catch (error) {
      console.error('绑定手机号失败:', error)
      return { success: false, message: error.message || '绑定失败' }
    }
  }

  // 推荐相关方法
  const generateReferralLink = async () => {
    try {
      const response = await apiGenerateReferralLink()
      // 转换响应格式：{code: 200, data: {...}} -> {success: true, data: {...}}
      if (response.code === 200) {
        return { success: true, data: response.data }
      } else {
        return { success: false, message: response.message || '生成推荐链接失败' }
      }
    } catch (error) {
      console.error('生成推荐链接失败:', error)
      return { success: false, message: error.message || '生成推荐链接失败' }
    }
  }

  const validateReferral = async (encryptedReferrerId) => {
    try {
      const response = await apiValidateReferral(encryptedReferrerId)
      // 转换响应格式
      if (response.code === 200) {
        return { success: true, data: response.data }
      } else {
        return { success: false, message: response.message || '验证推荐链接失败' }
      }
    } catch (error) {
      console.error('验证推荐链接失败:', error)
      return { success: false, message: error.message || '验证推荐链接失败' }
    }
  }

  const getReferralStats = async () => {
    try {
      const response = await apiGetReferralStats()
      // 转换响应格式
      if (response.code === 200) {
        return { success: true, data: response.data }
      } else {
        return { success: false, message: response.message || '获取推荐统计失败' }
      }
    } catch (error) {
      console.error('获取推荐统计失败:', error)
      return { success: false, message: error.message || '获取推荐统计失败' }
    }
  }

  const getReferrals = async (params) => {
    try {
      const response = await apiGetReferrals(params)
      // 转换响应格式
      if (response.code === 200) {
        return { success: true, data: response.data }
      } else {
        return { success: false, message: response.message || '获取推荐列表失败' }
      }
    } catch (error) {
      console.error('获取推荐列表失败:', error)
      return { success: false, message: error.message || '获取推荐列表失败' }
    }
  }

  // 生成推荐二维码 - 简化版本，直接在前端生成URL
  const generateQRCode = async (encryptedUserID) => {
    try {
      // 直接在前端生成二维码URL，不需要调用后端
      const qrCodeUrl = `/api/referral/qrcode?data=${encodeURIComponent(encryptedUserID)}`
      return { 
        success: true, 
        data: { 
          qr_code_url: qrCodeUrl 
        } 
      }
    } catch (error) {
      console.error('生成推荐二维码失败:', error)
      return { success: false, message: error.message || '生成推荐二维码失败' }
    }
  }

  return {
    // 状态
    token,
    userInfo,
    isLoggedIn,
    isWechatUser,
    
    // 方法
    setToken,
    setUserInfo,
    clearToken,
    clearUserInfo,
    handleLogin,
    handleRegister,
    handleLogout,
    logout, // 保持兼容性的logout方法
    fetchUserInfo,
    handleSendSMS,
    handleBindPhoneForWechatUser,
    
    // 推荐相关方法
    generateReferralLink,
    validateReferral,
    getReferralStats,
    getReferrals,
    generateQRCode
  }
})
